import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { ChakraProvider, Box, Heading, Spinner, Center } from '@chakra-ui/react';
import { GameProvider, useGame } from './contexts/GameContext.tsx'; 
import { SocketProvider, useSocket } from './contexts/SocketContext.tsx';
import { SoundProvider } from './contexts/SoundContext.tsx';
import { auth, signOut } from './config/firebase.ts';
import { onAuthStateChanged } from 'firebase/auth';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Category } from './types/game.ts';
import { checkAppVersion } from './utils/versionCheck.ts';

// Composants chargés immédiatement (critiques pour le premier rendu)
import AppLayout from './components/Layout/AppLayout.tsx';
import theme from './config/theme.ts';
import { initGA, trackPageView, checkAnalyticsConsent, trackGameEvents } from './utils/analytics.ts';
import SeasonalThemeProvider from './components/Common/SeasonalThemeProvider.tsx';
import DeletionPendingModal from './components/Profile/DeletionPendingModal.tsx';
import { API_URL } from './config/api.ts';
import './App.css';

// Composants conditionnels (lazy car pas toujours affichés)
const AuthPage = lazy(() => import('./components/Auth/AuthPage.tsx'));
const NicknameModal = lazy(() => import('./components/Auth/NicknameModal.tsx'));
const CookieConsentBanner = lazy(() => import('./components/Legal/CookieConsentBanner.tsx'));

// Redirect /syncduel/:code → /smartclash/:code (anciens liens)
const SyncDuelRedirect = ({ suffix = '' }: { suffix?: string }) => {
  const { code } = useParams<{ code: string }>();
  return <Navigate to={`/smartclash/${code}${suffix}`} replace />;
};

// Fallback de chargement pour les routes lazy
const LazyFallback = () => (
  <Center minH="60vh">
    <Spinner size="xl" color="var(--bleu-electrique)" thickness="4px" />
  </Center>
);

// ─── Lazy-loaded route components ───────────────────────────────────
const AdminDashboard = lazy(() => import('./pages/AdminDashboard.tsx'));
const LobbyPage = lazy(() => import('./components/Lobby/LobbyPageV2.tsx'));
const GameSetup = lazy(() => import('./components/Game/GameSetup.tsx'));
const Game = lazy(() => import('./components/Game/Game.tsx'));
const MultiplayerGameWrapper = lazy(() => import('./components/Game/MultiplayerGameWrapper.tsx'));
const TermsOfService = lazy(() => import('./components/Legal/TermsOfService.tsx'));
const PrivacyPolicy = lazy(() => import('./components/Legal/PrivacyPolicy.tsx'));
// Daily Streak
const DailyStreakPage = lazy(() => import('./components/Daily/DailyStreakPage.tsx'));
const DailyStreakLeaderboard = lazy(() => import('./components/Daily/DailyStreakLeaderboard.tsx'));
// Christmas
const ChristmasCalendar = lazy(() => import('./components/Christmas/ChristmasCalendar.tsx'));
const ChristmasDayQuiz = lazy(() => import('./components/Christmas/ChristmasDayQuiz.tsx'));
const ChristmasLeaderboard = lazy(() => import('./components/Christmas/ChristmasLeaderboard.tsx'));
// SyncDuel
const SyncDuelLobby = lazy(() => import('./components/SyncDuel/SyncDuelLobby.tsx'));
const SyncDuelPlay = lazy(() => import('./components/SyncDuel/SyncDuelPlay.tsx'));
const SyncDuelGameOver = lazy(() => import('./components/SyncDuel/SyncDuelGameOver.tsx'));
// Valentine
const ValentineHub = lazy(() => import('./components/Valentine/ValentineHub.tsx'));
const ValentineDailyDuo = lazy(() => import('./components/Valentine/ValentineDailyDuo.tsx'));
const ValentineCompatibility = lazy(() => import('./components/Valentine/ValentineCompatibility.tsx'));
// Weekly
const WeeklyHub = lazy(() => import('./components/Weekly/WeeklyHub.tsx'));
const WeeklyGame = lazy(() => import('./components/Weekly/WeeklyGame.tsx'));
const WeeklyCompleted = lazy(() => import('./components/Weekly/WeeklyCompleted.tsx'));
// SyncDuel context (lazy car utilisé uniquement dans les routes SyncDuel)
const SyncDuelSocketProvider = lazy(() =>
  import('./contexts/SyncDuelSocketContext.tsx').then(m => ({ default: m.SyncDuelSocketProvider }))
);
// ColorClash
const RoyalQuizPage = lazy(() => import('./components/RoyalQuiz/RoyalQuizPage.tsx'));
const RoyalQuizProvider = lazy(() =>
  import('./contexts/RoyalQuizContext.tsx').then(m => ({ default: m.RoyalQuizProvider }))
);
const ColorClashLobby = lazy(() => import('./components/ColorClash/ColorClashLobby.tsx'));
const ColorClashGame = lazy(() => import('./components/ColorClash/ColorClashGame.tsx'));
const ColorClashResults = lazy(() => import('./components/ColorClash/ColorClashResults.tsx'));
const ColorClashProvider = lazy(() =>
  import('./contexts/ColorClashContext.tsx').then(m => ({ default: m.ColorClashProvider }))
);

// Hook personnalisé pour gérer l'authentification
const useAuth = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser: any) => {
      setUser(currentUser);
      setLoading(false);
      
      // Tracker la connexion de l'utilisateur
      if (currentUser) {
        trackGameEvents.userLoggedIn(currentUser.isAnonymous ? 'anonymous' : 'google');
        
        // Vérifier si l'utilisateur a déjà défini son pseudo
        const hasSeenModal = localStorage.getItem(`nickname_set_${currentUser.uid}`) === 'true';
        
        // Au refresh/retour : n'afficher que si pas de pseudo ET jamais vu la modal
        // (les comptes existants ont déjà un displayName → pas de modal)
        if (!currentUser.isAnonymous && !hasSeenModal && (!currentUser.displayName || currentUser.displayName.trim() === '')) {
          setShowNicknameModal(true);
        }

        // Fetch admin status for non-anonymous users
        if (!currentUser.isAnonymous) {
          currentUser.getIdToken().then((token: string) =>
            fetch(`${API_URL}/api/user/me`, {
              headers: { Authorization: `Bearer ${token}` },
            })
              .then((r: Response) => r.json())
              .then((data: any) => setIsAdmin(data.isAdmin || false))
              .catch(() => {})
          );
        }
      } else {
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    signOut(auth).catch(console.error);
  };

  const handleLoginSuccess = (loggedInUser: any) => {
    setUser(loggedInUser);
    // Tracker l'inscription/connexion
    const method = loggedInUser.isAnonymous ? 'anonymous' : 'google';
    trackGameEvents.userRegistered(method);

    // Afficher la modal uniquement pour les comptes NOUVELLEMENT créés
    const hasSeenModal = localStorage.getItem(`nickname_set_${loggedInUser.uid}`) === 'true';
    const isNewAccount = loggedInUser.metadata?.creationTime &&
      (Date.now() - new Date(loggedInUser.metadata.creationTime).getTime()) < 30000;

    if (!loggedInUser.isAnonymous && !hasSeenModal && isNewAccount) {
      setShowNicknameModal(true);
    }
  };
  
  const handleNicknameModalClose = () => {
    setShowNicknameModal(false);
    // Recharger l'utilisateur pour obtenir le nouveau displayName
    if (auth.currentUser) {
      auth.currentUser.reload().then(() => {
        setUser({ ...auth.currentUser });
      });
    }
  };

  return { user, loading, handleLogout, handleLoginSuccess, showNicknameModal, handleNicknameModalClose, isAdmin };
};

// Redirection automatique pour /join/:code
function JoinLobbyRedirect() {
  const { code } = useParams();
  const navigate = useNavigate();
  
  React.useEffect(() => {
    if (code) {
      // Rediriger directement vers /lobby/:code
      // La route /lobby/:code gérera l'affichage de la page de connexion si nécessaire
      navigate(`/lobby/${code}`, { replace: true });
    }
  }, [code, navigate]);
  
  return <div>Redirection...</div>;
}

// Ce conteneur interne gère l'affichage une fois que l'utilisateur est connecté.
const MainContainer = () => {
  const { user, handleLogout, isAdmin } = useAuth();
  const { code: urlLobbyCode } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const [activeScreen, setActiveScreen] = useState('lobby');
  const [multiplayerData, setMultiplayerData] = useState<any>(null);
  const { initializeGame, state: gameState } = useGame();
  const socket = useSocket();

  // Ne plus charger automatiquement les catégories ici
  // Les catégories seront chargées uniquement quand nécessaire dans les composants de jeu
  
  const handleStartGame = async (
    players: { id: number; name: string }[],
    streakToWin: number,
    categoriesToWin: number,
    difficultyRange: [number, number],
    selectedCategories: Category[],
  mysteryAttackMode: 'classic' | 'roulette',
  advanced?: { classicMalusValue?: number; roulettePowers?: Record<string, { enabled: boolean; weight: number }>; enableCategoryTimer?: boolean; categoryTimerSeconds?: number; enableQuestionTimer?: boolean; questionTimerSeconds?: number; }
  ) => {
    console.log('🎮 [App] Démarrage de la partie avec:', {
      players: players.length,
      streakToWin,
      categoriesToWin,
      difficultyRange,
      selectedCategoriesCount: selectedCategories?.length || 0
    });

    // Toujours utiliser les catégories sélectionnées (GameSetup s'assure qu'il y en a)
    if (selectedCategories && selectedCategories.length > 0) {
  console.log('📦 [App] Utilisation des catégories sélectionnées:', selectedCategories.map(c => c.name));
  initializeGame(players, streakToWin, categoriesToWin, difficultyRange, selectedCategories, mysteryAttackMode, advanced);
      setActiveScreen('game');
      
      // Tracker le démarrage du jeu local
      trackGameEvents.gameStarted('local', players.length);
    } else {
      console.error('❌ [App] Aucune catégorie sélectionnée - cela ne devrait pas arriver');
    }
  };

  const handleStartMultiplayerGame = (data: any) => {
    if (socket) {
      console.log('🎮 Démarrage du jeu multijoueur avec les règles:', data.rules);
      socket.startMultiplayerGame(data.lobbyCode, data.rules, data.players, user);
    }
    setMultiplayerData(data);
    setActiveScreen('multiplayer');
    
    // Tracker le démarrage du jeu multijoueur
    trackGameEvents.gameStarted('multiplayer', data.players.length);
  };

  const handleBackToLobby = (code?: string) => {
    setActiveScreen('lobby');
    setMultiplayerData(null);
    // Rediriger vers la room si un code est fourni, sinon vers l'accueil
    if (code) {
      navigate(`/lobby/${code}`);
    } else {
      navigate('/');
    }
  };
  
  useEffect(() => {
    if (gameState.isGameOver) {
      setActiveScreen('game');
    }
  }, [gameState.isGameOver]);

  if (!user) return null;

  switch (activeScreen) {
    case 'gameSetup':
      return <GameSetup onStartGame={handleStartGame} />;
    case 'game':
      return <Game />;
    case 'multiplayer':
      return multiplayerData ? (
        <MultiplayerGameWrapper
          lobbyCode={multiplayerData.lobbyCode}
          players={multiplayerData.players}
          rules={multiplayerData.rules}
          currentUser={multiplayerData.currentUser}
          hostName={multiplayerData.hostName}
          onBackToLobby={handleBackToLobby}
        />
      ) : (
        <div>Erreur: Données multijoueur manquantes</div>
      );
      case 'lobby':
      default:
        return <LobbyPage
          user={user}
          handleLogout={handleLogout}
          onStartLocalGame={() => setActiveScreen('gameSetup')}
          onStartMultiplayerGame={handleStartMultiplayerGame}
          urlLobbyCode={urlLobbyCode} // Passe explicitement le code
          isAdmin={isAdmin}
        />;
    }
  }

function App() {
  const { user, loading, handleLoginSuccess, showNicknameModal, handleNicknameModalClose, isAdmin } = useAuth();
  const location = useLocation();

  const [deletionStatus, setDeletionStatus] = useState<{
    pending: boolean;
    requestedAt?: string;
    scheduledFor?: string;
  } | null>(null);

  const deletionCheckedRef = useRef(false);

  useEffect(() => {
    if (!user) {
      deletionCheckedRef.current = false;
      return;
    }
    if (user.isAnonymous || deletionCheckedRef.current) return;
    deletionCheckedRef.current = true;
    // One-time check per session — fire-and-forget
    user.getIdToken().then((token: string) =>
      fetch(`${API_URL}/api/user/deletion-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then((data: any) => setDeletionStatus(data))
        .catch(() => {})
    );
  }, [user]);

  // Vérifier la version de l'application au démarrage
  useEffect(() => {
    checkAppVersion();
  }, []);

  // Initialiser Google Analytics si le consentement a été donné
  useEffect(() => {
    const consent = checkAnalyticsConsent();
    if (consent === true) {
      initGA('G-LFY1SNQK9J');
    }
  }, []);

  // Tracker les changements de page
  useEffect(() => {
    const consent = checkAnalyticsConsent();
    if (consent === true) {
      trackPageView(location.pathname + location.search);
    }
  }, [location]);

  return (
    <SeasonalThemeProvider>
    <ChakraProvider theme={theme}>
      <SoundProvider>
        <SocketProvider>
          <GameProvider>
            <AppLayout>
              <Box minH="100vh" className="app-container">
                {loading ? (
                  <Heading color="purple.600">Chargement...</Heading>
                ) : (
                  <>
                    <Suspense fallback={<LazyFallback />}>
                    <Routes>
                      <Route path="/login" element={<AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/join/:code" element={<JoinLobbyRedirect />} />
                      <Route path="/lobby/:code" element={user ? <MainContainer /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      {/* Daily Streak */}
                      <Route path="/daily" element={user ? <DailyStreakPage /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/daily/leaderboard" element={user ? <DailyStreakLeaderboard /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      {/* Christmas Advent Calendar 2025 */}
                      <Route path="/christmas" element={user ? <ChristmasCalendar /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/christmas/day/:day" element={user ? <ChristmasDayQuiz /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/christmas/leaderboard" element={user ? <ChristmasLeaderboard /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      {/* Redirects syncduel → smartclash (anciens liens) */}
                      <Route path="/syncduel/:code" element={<SyncDuelRedirect />} />
                      <Route path="/syncduel/:code/play" element={<SyncDuelRedirect suffix="/play" />} />
                      <Route path="/syncduel/:code/over" element={<SyncDuelRedirect suffix="/over" />} />
                      {/* SmartClash (ex SyncDuel — 1v1 temps réel) */}
                      <Route path="/smartclash/:code" element={user ? <Suspense fallback={<LazyFallback />}><SyncDuelSocketProvider><SyncDuelLobby /></SyncDuelSocketProvider></Suspense> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/smartclash/:code/play" element={user ? <Suspense fallback={<LazyFallback />}><SyncDuelSocketProvider><SyncDuelPlay /></SyncDuelSocketProvider></Suspense> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/smartclash/:code/over" element={user ? <Suspense fallback={<LazyFallback />}><SyncDuelSocketProvider><SyncDuelGameOver /></SyncDuelSocketProvider></Suspense> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      {/* ColorClash — team grid conquest */}
                      <Route path="/colorclash/:code" element={user ? <Suspense fallback={<LazyFallback />}><ColorClashProvider><ColorClashLobby /></ColorClashProvider></Suspense> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/colorclash/:code/play" element={user ? <Suspense fallback={<LazyFallback />}><ColorClashProvider><ColorClashGame /></ColorClashProvider></Suspense> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/colorclash/:code/results" element={user ? <Suspense fallback={<LazyFallback />}><ColorClashProvider><ColorClashResults /></ColorClashProvider></Suspense> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      {/* RoyalQuiz — battle royale quiz */}
                      <Route path="/royalquiz" element={user && !user.isAnonymous ? <Suspense fallback={<LazyFallback />}><RoyalQuizProvider><RoyalQuizPage /></RoyalQuizProvider></Suspense> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      {/* Valentine's Day Special Mode 💕 */}
                      <Route path="/valentine" element={user ? <ValentineHub /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/valentine/daily" element={user ? <ValentineDailyDuo /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/valentine/compatibility" element={user ? <ValentineCompatibility /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/valentine/compatibility/:code" element={user ? <ValentineCompatibility /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/valentine/join/:code" element={user ? <ValentineHub /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      {/* Weekly Mode 🏆 */}
                      <Route path="/weekly" element={user ? <WeeklyHub /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/weekly/play" element={user ? <WeeklyGame /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      <Route path="/weekly/completed" element={user ? <WeeklyCompleted /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                      {/* Admin Dashboard */}
                      <Route path="/admin" element={user && isAdmin ? <Suspense fallback={<LazyFallback />}><AdminDashboard /></Suspense> : <Navigate to="/" />} />
                      {/* Route par défaut : si connecté, MainContainer, sinon AuthPage */}
                      <Route path="*" element={user ? <MainContainer /> : <AuthPage onLoginSuccess={handleLoginSuccess} />} />
                    </Routes>
                    </Suspense>
                    
                    {/* Modal de choix de pseudo pour première connexion */}
                    {user && showNicknameModal && (
                      <NicknameModal
                        isOpen={showNicknameModal}
                        onClose={handleNicknameModalClose}
                      />
                    )}

                    {deletionStatus?.pending && (
                      <DeletionPendingModal
                        isOpen={true}
                        requestedAt={deletionStatus.requestedAt!}
                        scheduledFor={deletionStatus.scheduledFor!}
                        onCancel={() => setDeletionStatus({ pending: false })}
                        onContinue={() => { setDeletionStatus({ pending: false }); signOut(auth); }}
                      />
                    )}

                    {/* Bannière de consentement des cookies */}
                    <CookieConsentBanner 
                      onAccept={() => {
                        initGA('G-LFY1SNQK9J');
                        trackPageView(location.pathname + location.search);
                      }}
                    />
                  </>
                )}
              </Box>
            </AppLayout>
          </GameProvider>
        </SocketProvider>
      </SoundProvider>
    </ChakraProvider>
    </SeasonalThemeProvider>
  );
}

export default App;